import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import tippy, { Instance, Props } from 'tippy.js';
import { IRxntTooltipOptions } from './rxnt-tooltip-options.model';

// https://stackoverflow.com/a/67876329/897326
@Directive({
  selector: '[tooltip],[tooltipOptions]',
  standalone: false,
})
export class TooltipDirective implements OnDestroy, AfterViewInit, OnChanges {
  constructor(private readonly el: ElementRef) {
  }

  private instance: Instance<Props> | null = null;

  /**
   * Specifies the tooltip content. If name collision occurs, instead use `tooltipAlias` from TooltipAliasDirective.
   */
  @Input() tooltip: string | undefined = '';
  /**
   * Specifies the tooltip options. If name collision occurs, instead use `tooltipOptionsAlias` from TooltipAliasDirective.
   */
  @Input() tooltipOptions: IRxntTooltipOptions = {};

  ngAfterViewInit() {
    this.instance = tippy(this.el.nativeElement as Element, {});

    const content: string = this.escapeContent(this.tooltip);

    this.updateProps({
      ...(this.tooltipOptions),
      content: content,
      allowHTML: true,
    });
  }

  ngOnDestroy() {
    this.instance?.destroy();
    this.instance = null;
  }

  ngOnChanges(changes: SimpleChanges) {
    let props = {
      ...(this.tooltipOptions),
      content: this.tooltip,
    };

    if (changes['tooltipOptions']) {
      props = { ...(changes['tooltipOptions'].currentValue), content: this.tooltip };
    }
    if (changes['tooltip']) {
      props.content = this.escapeContent(this.tooltip);
    }

    this.updateProps(props);
  }

  private updateProps(props: Partial<Props>) {
    if (this.instance && !jsonEqual<any>(props, this.instance.props)) {
      this.instance.setProps(this.normalizeOptions(props));
      if (!props.content) {
        this.instance.disable();
      } else {
        this.instance.enable();
      }
    }
  }

  private normalizeOptions = (props: Partial<Props>): Partial<Props> => ({
    ...(props),
    duration: props?.duration ?? [ 50, 50 ],
  });

  private escapeHtml(unsafe: number | string | null | undefined): string {
    if (unsafe == null) {
      return '';
    }

    return unsafe.toString()
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  private escapeContent(unsafe: number | string | null | undefined): string {
    const sanitizedTooltip: string = this.escapeHtml(unsafe);
    const content: string = sanitizedTooltip.replace(/\n/g, '<br>');

    if (!content) {
      return '';
    }

    if (this.tooltipOptions.preformatted) {
      return '<pre>' + content + '</pre>';
    }

    return content;
  }
}

export const jsonEqual = <T>(aa: T, bb: T): boolean => JSON.stringify(aa) === JSON.stringify(bb);
